import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import {poster1} from "../../assets";
import {poster2} from "../../assets";

interface Singer {
  id: string;
  name: string;
  genre: string;
  image: string;
  projects: {
    id: string;
    title: string;
    thumbnail: string;
    videoUrl: string;
    year: string;
  }[];
}

const defaultSinger: Singer = {
    id: "default-1",
    name: "Jassi Gill",
    genre: "Playback Singer",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
    projects: [
      { id: "p1", title: "Nikle Currant", thumbnail: poster1, videoUrl: "https://www.youtube.com/watch?v=example1", year: "2024" },
      { id: "p2", title: "Bapu Zimidar", thumbnail: poster2, videoUrl: "https://www.youtube.com/watch?v=example2", year: "2023" },
    ]
};

interface SingersCarouselProps {
  singers?: Singer[];
}

export function SingersCarousel ({ singers = [] }: SingersCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSinger, setSelectedSinger] = useState<Singer | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const displaySingers = singers && singers.length > 0 ? singers : [defaultSinger];

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying || selectedSinger || displaySingers.length === 0) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % displaySingers.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, selectedSinger, displaySingers.length]);

  const handlePrev = () => {
    if (displaySingers.length === 0) return;
    setIsAutoPlaying(false);
    setActiveIndex((prev) => (prev - 1 + displaySingers.length) % displaySingers.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleNext = () => {
    if (displaySingers.length === 0) return;
    setIsAutoPlaying(false);
    setActiveIndex((prev) => (prev + 1) % displaySingers.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleSingerClick = (singer: Singer, index: number) => {
    setIsAutoPlaying(false);
    if (index === activeIndex) {
      setSelectedSinger(singer);
    } else {
      setActiveIndex(index);
    }
  };

  const getVisibleSingers = () => {
    if (displaySingers.length === 0) return [];
    // Ensure we have enough items to show 5 centered
    // If not enough items, we just repeat them in the logic or just map what we have if very few.
    // However, the carousel logic assumes at least some items.
    // Let's stick to the circular logic.
    const result = [];
    for (let i = -2; i <= 2; i++) {
        // Handle negative modulo correctly
      const index = (activeIndex + i + displaySingers.length * 10) % displaySingers.length; 
      result.push({ singer: displaySingers[index], position: i, originalIndex: index });
    }
    return result;
  };

  const visibleSingers = getVisibleSingers();

  if (displaySingers.length === 0) return null;

  return (
    <section className="relative py-24 overflow-hidden">
     

      <div className="relative z-10 container mx-auto px-4">
        <div className="text-center mb-16">
          <p className="text-primary font-medium tracking-[0.2em] uppercase mb-4">
            Collaborations
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground font-display">
            Artists We've Worked With
          </h2>
        </div>

        {/* Carousel with Center Focus Effect */}
        <div className="relative flex items-center justify-center min-h-[320px]">
          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-4 md:left-12 z-20 w-12 h-12 rounded-full border border-primary/30 bg-background/50 backdrop-blur-sm flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
            aria-label="Previous singer"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Singers Display */}
          <div className="flex items-center justify-center gap-4 md:gap-8 px-16 md:px-24">
            {visibleSingers.map(({ singer, position, originalIndex }, idx) => {
              // Ensure we use a unique key if singers are duplicated in visibleSingers (e.g. if length < 5)
              // But here we're mapping logic indices, so unique key should be combination.
              // Actually singer.id is better but circular logic might show same singer multiple times if we had < 5 items?
              // The logic `(activeIndex + i + length) % length` picks indices.
              // If length is 1, all 5 visible are index 0.
              
              const isActive = position === 0;
              const isAdjacent = Math.abs(position) === 1;
              const isEdge = Math.abs(position) === 2;

              return (
                <div
                  key={`${singer.id}-${idx}`}
                  className={`flex flex-col items-center transition-all duration-500 ease-out cursor-pointer ${
                    isEdge ? 'hidden md:flex' : ''
                  }`}
                  onClick={() => handleSingerClick(singer, originalIndex)}
                  style={{
                    transform: isActive 
                      ? 'scale(1)' 
                      : isAdjacent 
                        ? 'scale(0.85)' 
                        : 'scale(0.7)',
                    opacity: isActive ? 1 : isAdjacent ? 0.7 : 0.5,
                    zIndex: isActive ? 10 : isAdjacent ? 5 : 1,
                  }}
                >
                  {/* Singer Image */}
                  <div 
                    className={`relative rounded-full overflow-hidden transition-all duration-500 ${
                      isActive 
                        ? 'w-36 h-36 md:w-44 md:h-44 ring-4 ring-primary shadow-[0_0_40px_rgba(245,158,11,0.4)]' 
                        : isAdjacent
                          ? 'w-28 h-28 md:w-32 md:h-32 ring-2 ring-primary/40'
                          : 'w-20 h-20 md:w-24 md:h-24 ring-1 ring-primary/20'
                    }`}
                  >
                    <img
                      src={singer.image}
                      alt={singer.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Glow effect for active */}
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
                    )}
                  </div>

                  {/* Singer Info */}
                  <div className={`text-center mt-4 transition-all duration-500 ${
                    isActive ? 'opacity-100' : 'opacity-60'
                  }`}>
                    <h3 className={`font-bold text-foreground font-display transition-all duration-500 ${
                      isActive ? 'text-lg md:text-xl' : 'text-sm md:text-base'
                    }`}>
                      {singer.name}
                    </h3>
                    <p className={`text-muted-foreground transition-all duration-500 ${
                      isActive ? 'text-sm' : 'text-xs'
                    }`}>
                      {singer.genre}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={handleNext}
            className="absolute right-4 md:right-12 z-20 w-12 h-12 rounded-full border border-primary/30 bg-background/50 backdrop-blur-sm flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
            aria-label="Next singer"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {displaySingers.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsAutoPlaying(false);
                setActiveIndex(index);
                setTimeout(() => setIsAutoPlaying(true), 10000);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === activeIndex
                  ? "bg-primary w-8"
                  : "bg-primary/30 hover:bg-primary/50"
              }`}
              aria-label={`Go to singer ${index + 1}`}
            />
          ))}
        </div>

        {/* Selected Singer Projects */}
        {selectedSinger && (
          <div className="mt-16 animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-foreground font-display">
                Projects with {selectedSinger.name}
              </h3>
              <button
                onClick={() => {
                  setSelectedSinger(null);
                  setIsAutoPlaying(true);
                }}
                className="text-primary hover:text-primary/80 transition-colors text-sm"
              >
                Close
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {selectedSinger.projects.map((project) => (
                <a
                  key={project.id}
                  href={project.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative rounded-xl overflow-hidden aspect-video bg-secondary"
                >
                  <img
                    src={project.thumbnail}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center">
                      <Play className="w-6 h-6 text-primary-foreground fill-primary-foreground" />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="text-foreground font-semibold text-lg">
                      {project.title}
                    </h4>
                    <p className="text-muted-foreground text-sm">{project.year}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// export default SingersCarousel;
